/**
 * Manages the registration of the service worker and web push notifications.
 */
import * as deviceDetect from "deviceDetect";
import readlangClient from "readlangClient";

type DeviceSubscriptionStatus =
  | {
      type: "haveSubscription";
      subscription: any;
      device: string;
    }
  | {
      type: "noSubscription";
    }
  | {
      type: "notSupported";
    };

type Permission = "granted" | "denied" | "default" | "notSupported";

export interface ServerSubscription {
  _id: string;
  userID: string;
  subscriptionData: {
    endpoint: string;
    keys: {
      auth: string;
      p256dh: string;
    };
  };
  device: string;
}

let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

// __VAPID_PUBLIC_KEY is injected by rollup from the corresponding environment variable
// @ts-ignore
const vapidPublicKey = __VAPID_PUBLIC_KEY;

const isSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  !/YandexBot/.test(navigator.userAgent);

const initialize = async () => {
  if (isSupported()) {
    // Set up service worker
    serviceWorkerRegistration = await navigator.serviceWorker.register(
      `/serviceWorker.js?cacheBust=${window.READLANG.gitCommit}`
    );

    // Update the service worker.
    await serviceWorkerRegistration.update();
  }
};

initialize();

/** @returns true if succeeded, false if not */
export const subscribe = async (device): Promise<boolean> => {
  let subscriptionData;
  try {
    subscriptionData = await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidPublicKey,
    });
  } catch (error) {
    console.error("Error subscribing to push notifications: ", error);
    return false;
  }

  await readlangClient.request("POST", "/api/webPushNotifications/subscribe", {
    subscriptionData,
    device,
  });

  return true;
};

export const reactivateSubscription = async (subscription) => {
  return await readlangClient.request(
    "POST",
    "/api/webPushNotifications/subscribe",
    subscription
  );
};

export const getSubscriptionStatus =
  async (): Promise<DeviceSubscriptionStatus> => {
    if (!isSupported()) {
      return { type: "notSupported" };
    }

    if (!serviceWorkerRegistration) {
      throw new Error("serviceWorkerRegistration not initialized");
    }

    const subscription =
      await serviceWorkerRegistration.pushManager.getSubscription();

    if (!subscription) {
      return { type: "noSubscription" };
    }

    return {
      type: "haveSubscription",
      subscription: subscription.toJSON(),
      device: deviceDetect.deviceString(),
    };
  };

export const getServerSubscriptions = async (
  thisDeviceStatus: DeviceSubscriptionStatus
): Promise<(ServerSubscription & { thisDevice?: boolean })[]> => {
  const subscriptions = (
    await readlangClient.request(
      "GET",
      "/api/webPushNotifications/subscriptions",
      {}
    )
  ).data.subscriptions as (ServerSubscription & { thisDevice?: boolean })[];

  if (thisDeviceStatus.type === "haveSubscription") {
    // Look for a subscription matching this device and mark it as such
    for (const subscription of subscriptions) {
      const subscriptionData = subscription.subscriptionData;
      if (
        subscriptionData.endpoint === thisDeviceStatus.subscription.endpoint &&
        subscriptionData.keys.auth ===
          thisDeviceStatus.subscription.keys.auth &&
        subscriptionData.keys.p256dh ===
          thisDeviceStatus.subscription.keys.p256dh
      ) {
        subscription.thisDevice = true;
      }
    }
  }

  return subscriptions;
};

export const sendNotification = async (
  title: string,
  body: string,
  badgeCount: number
) => {
  await readlangClient.request(
    "POST",
    "/api/webPushNotifications/sendNotification",
    { title, body, icon: "/favicon-196.png", badgeCount }
  );
};

export const currentPermission = (): Permission => {
  return isSupported() ? Notification.permission : "notSupported";
};

export const enableWebNotifications = async () => {
  return await Notification.requestPermission();
};

export const removeSubscription = async (subscriptionID: string) => {
  await readlangClient.request(
    "DELETE",
    `/api/webPushNotifications/subscription/${subscriptionID}`,
    {}
  );
};
